import React, { useEffect } from 'react';
import { string, arrayOf, shape, bool } from 'prop-types';
import { ComponentSelectorWrapper } from './ComponentSelectorWrapper';

export function ComponentSelector({
  defaultComponent,
  variations,
  backgroundColor,
  componentMap,
  variationIds,
  renderDefault,
  isSeoBot,
}) {
  useEffect(() => {
    if (window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('component-selector.ready');
    }
  }, []);

  const publishEvent = ({ ...props }) => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('component-selector.selected', { ...props });
    }
  };

  const isSSR = typeof window === 'undefined';
  const noVariations = variationIds.length < 1;

  const defaultProps = {
    ...defaultComponent,
    componentId: defaultComponent?.id,
    componentClass: defaultComponent?.__typename,
  };
  const DefaultComponent = componentMap[defaultComponent?.__typename];

  if (isSSR) {
    return (
      <ComponentSelectorWrapper backgroundColor={backgroundColor} showPlaceholder={!isSeoBot}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {DefaultComponent && <DefaultComponent {...defaultProps} />}
      </ComponentSelectorWrapper>
    );
  }
  // This is for the initial render client-side, before the useEffect kicks in
  if (!renderDefault && noVariations) {
    return (
      <ComponentSelectorWrapper backgroundColor={backgroundColor} showPlaceholder>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {DefaultComponent && <DefaultComponent {...defaultProps} />}
      </ComponentSelectorWrapper>
    );
  }
  // Skip searching through the variations if there were no offers returned from Adobe and render the default
  if (renderDefault) {
    publishEvent({ ...defaultProps });

    return (
      <ComponentSelectorWrapper backgroundColor={backgroundColor}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {DefaultComponent && <DefaultComponent {...defaultProps} />}
      </ComponentSelectorWrapper>
    );
  }
  // Find a variation using the offers from Adobe, or use the default if there is no such match
  const variation = variations.find(({ id }) => variationIds.includes(id));
  const { __typename, id, ...props } = variation || defaultComponent || {};
  const Component = componentMap[__typename];
  const componentProps = { ...props, componentId: id, componentClass: __typename };

  publishEvent({ ...componentProps });

  return (
    <ComponentSelectorWrapper backgroundColor={backgroundColor}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      {Component && <Component {...componentProps} />}
    </ComponentSelectorWrapper>
  );
}

ComponentSelector.displayName = 'ComponentSelector';

ComponentSelector.propTypes = {
  variations: arrayOf(shape({})).isRequired,
  defaultComponent: shape({}).isRequired,
  backgroundColor: string,
  componentMap: shape({}),
  variationIds: arrayOf(string),
  renderDefault: bool,
  isSeoBot: bool,
};

ComponentSelector.defaultProps = {
  backgroundColor: 'white',
  componentMap: {},
  variationIds: [],
  renderDefault: false,
  isSeoBot: false,
};
